import { IJSONSchema } from '../../../plugins/validation/IJSONSchema';
import IValidationError from '../../../plugins/validation/IValidationError';
import Validation from '../../../plugins/validation/validation';

/**
 * Copy setters and getter from prototype to actual instance, this has several advantages
 * This way the properties will be enumerable and they will survive a spread
 * This should work with transpiled and native ES6 getter/setter variants (tested)
 * As this is a class it will always have a prototype
 * */
function hoistGettersAndSetters(classInstance: any) {
  const primitivePropNames = Object.getOwnPropertyNames(Object.prototype);
  const prototypeChain = [];
  const queue = [Object.getPrototypeOf(classInstance)];
  // Traverse the prototype chain (needed for inheritance)
  while (queue.length > 0) {
    const currObj = queue.pop();
    prototypeChain.push(currObj);

    const nextObj = Object.getPrototypeOf(currObj);

    // Not the nicest solution but it works
    // It prevents the ModelBase properties being copied directly onto the model instance
    // TODO: Make this compile-time checked
    const isBaseModel = nextObj?.constructor?.name === 'ServiceModelBase';

    // Should not cause an endless loop for self-referential chains
    if (!isBaseModel && nextObj && !prototypeChain.includes(nextObj)) {
      queue.push(nextObj);
    }
  }

  // Must reverse order for proper shadowing of members
  prototypeChain.reverse();

  prototypeChain.forEach((currObj) => {
    const propnames = Object.getOwnPropertyNames(currObj);
    propnames.forEach((propName) => {
      if (primitivePropNames.includes(propName)) {
        // Skip any propNames that are part of JS
        return;
      }
      const descriptor = Object.getOwnPropertyDescriptor(currObj, propName);
      const hasGetterOrSetter = descriptor?.get || descriptor?.set;
      if (hasGetterOrSetter) {
        Object.defineProperty(classInstance, propName, {
          get: descriptor?.get?.bind(classInstance),
          set: descriptor?.set?.bind(classInstance),
          enumerable: true,
          configurable: true,
        });
      }
    });
  });
}

export default class ServiceModelBase {
  constructor() {
    hoistGettersAndSetters(this);
  }
  // Since the core framework should not be polluted by Vue
  // a static instance of the validator is used
  // This could be injected in the future
  static validator = new Validation();
  errors = [] as IValidationError[];
  schema = undefined as IJSONSchema | undefined;

  get valid() {
    if (!this.schema) {
      return true;
    }
    const errors = this.getValidationErrors(this.schema);
    return errors.length === 0;
  }

  protected getValidationErrors(schema: IJSONSchema, propName?: string): IValidationError[] {
    return ServiceModelBase.validator.validate(schema, this, propName);
  }

  public validateWithSchema(schema: IJSONSchema, propName?: string): boolean {
    let errors = this.getValidationErrors(schema, propName);
    if (propName !== undefined) {
      // Clear previous prop error
      this.errors = this.errors.filter((error) => error.propName !== propName && error.path?.join('.') !== propName);
      errors = [...this.errors, ...errors];
    }
    this.errors = errors;
    return this.errors.length === 0;
  }

  public validate(propName?: string): boolean {
    if (this.schema) {
      return this.validateWithSchema(this.schema, propName);
    }
    // eslint-disable-next-line no-console
    console.warn('validate was called on a model without defining a schema');
    return true;
  }
}
